import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "@/components/Layout";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  LayoutDashboard, Building2, Calendar, FileText, CreditCard, Sparkles,
  Search, Users, TrendingUp, IndianRupee, CheckCircle2, XCircle, Loader2, Save, Plus, Edit3,
  Pause, Play, Ban, Trash2, MoreVertical, ShieldCheck, Upload as UploadIcon, MessageCircle, Palette, Type, Link2, Zap,
  Gift as GiftIcon, Star as StarIcon, Volume2, VolumeX, FileImage,
} from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { AVAILABLE_FONTS, useSiteSettings } from "@/lib/settings";

/* ============ DASHBOARD ============ */
export function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [labs, setLabs] = useState([]);
  const [reports, setReports] = useState([]);
  const [ratings, setRatings] = useState({ total_reviews: 0, lab: { avg: 0, count: 0 }, platform: { avg: 0, count: 0 } });
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/labs").then((r) => setLabs(r.data || [])).catch(() => {});
    api.get("/admin/reports").then((r) => setReports(r.data || [])).catch(() => {});
    api.get("/admin/ratings/summary").then((r) => setRatings(r.data)).catch(() => {});
  }, []);
  const s = stats || { labs: 0, users: 0, reports_total: 0, reports_ready: 0, bookings: 0, mrr: 0 };

  const volume = useVolume(reports);

  return (
    <DashboardLayout role="super_admin">
      <h1 className="font-heading font-bold text-3xl text-ink">Platform overview</h1>
      <p className="text-ink/60">All EFGH-empanelled labs, their reports, bookings and billing.</p>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={Building2} color="text-efgh-orange" bg="bg-efgh-orange/10" value={s.labs} label="Labs empanelled" to="/admin/labs" testid="admin-kpi-labs" />
        <Kpi icon={FileText} color="text-efgh-blue" bg="bg-efgh-blue/10" value={s.reports_total} label="Reports total" to="/admin/reports" testid="admin-kpi-reports" />
        <Kpi icon={Calendar} color="text-efgh-green" bg="bg-efgh-green/10" value={s.bookings} label="Bookings" to="/admin/bookings" testid="admin-kpi-bookings" />
        <Kpi icon={IndianRupee} color="text-efgh-yellow" bg="bg-efgh-yellow/10" value={`₹${(s.mrr/1000).toFixed(1)}K`} label="Monthly recurring rev." to="/admin/billing" testid="admin-kpi-mrr" />
      </div>

      {/* Ratings — Lab vs EFGH platform, kept visually distinct so patients (and admins) never conflate them. */}
      <div className="mt-6 grid sm:grid-cols-3 gap-3" data-testid="admin-ratings-summary">
        <RatingKpi
          testid="rating-lab"
          label="Average lab rating"
          hint="Aggregated across all empanelled labs"
          avg={ratings.lab.avg}
          count={ratings.lab.count}
          icon={StarIcon}
          color="text-efgh-yellow"
          bg="bg-efgh-yellow/10"
        />
        <RatingKpi
          testid="rating-platform"
          label="EFGH platform rating"
          hint="How patients rate the Smart Report experience"
          avg={ratings.platform.avg}
          count={ratings.platform.count}
          icon={Sparkles}
          color="text-efgh-orange"
          bg="bg-efgh-orange/10"
        />
        <Card className="rounded-2xl p-5">
          <div className={`w-10 h-10 rounded-xl bg-efgh-blue/10 text-efgh-blue grid place-items-center`}><MessageCircle className="w-5 h-5" /></div>
          <div className="mt-3 font-heading font-bold text-3xl text-ink" data-testid="rating-total-count">{ratings.total_reviews || 0}</div>
          <div className="text-xs uppercase tracking-wider text-ink/50 mt-1">Total reviews collected</div>
        </Card>
      </div>

      <div className="mt-6 grid lg:grid-cols-[1.4fr_1fr] gap-6">
        <Card className="p-6 rounded-2xl">
          <div className="flex items-center justify-between">
            <div className="font-heading font-semibold text-ink">Report volume — last 14 days</div>
            <TrendingUp className="w-4 h-4 text-efgh-green" />
          </div>
          <div className="mt-3 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={volume}>
                <defs><linearGradient id="v2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#5A5AC7" stopOpacity={0.35} /><stop offset="100%" stopColor="#5A5AC7" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F4" />
                <XAxis dataKey="d" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={26} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Area type="monotone" dataKey="v" stroke="#5A5AC7" fill="url(#v2)" strokeWidth={2.5} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 rounded-2xl">
          <div className="font-heading font-semibold text-ink">Top labs by volume</div>
          <div className="mt-3 space-y-3">
            {labs.slice().sort((a, b) => (b.reports_count || 0) - (a.reports_count || 0)).slice(0, 6).map((l) => (
              <div key={l.id} className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-ink">{l.name}</div>
                  <div className="text-xs text-ink/50">{l.city} · {l.subscription_plan || "starter"}</div>
                </div>
                <div className="text-right">
                  <div className="font-heading font-semibold text-ink">{l.reports_count || 0}</div>
                  <div className="text-[10px] uppercase tracking-wider text-ink/50">reports</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}

function useVolume(reports) {
  const bins = {};
  const now = new Date();
  for (let i = 13; i >= 0; i--) {
    const d = new Date(now); d.setDate(now.getDate() - i);
    bins[d.toISOString().slice(0, 10)] = 0;
  }
  for (const r of reports) {
    const k = (r.created_at || "").slice(0, 10);
    if (k in bins) bins[k]++;
  }
  return Object.entries(bins).map(([k, v]) => ({ d: k.slice(5), v }));
}

function Kpi({ icon: Icon, color, bg, value, label, to, testid }) {
  const inner = (
    <Card className={`rounded-2xl p-5 ${to ? "card-hover cursor-pointer transition-transform hover:-translate-y-0.5" : ""}`} data-testid={testid}>
      <div className={`w-10 h-10 rounded-xl ${bg} ${color} grid place-items-center`}><Icon className="w-5 h-5" /></div>
      <div className="mt-3 font-heading font-bold text-3xl text-ink">{value}</div>
      <div className="text-xs uppercase tracking-wider text-ink/50 mt-1">{label}</div>
    </Card>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

function RatingKpi({ testid, label, hint, avg, count, icon: Icon, color, bg }) {
  return (
    <Card className="rounded-2xl p-5" data-testid={testid}>
      <div className={`w-10 h-10 rounded-xl ${bg} ${color} grid place-items-center`}><Icon className="w-5 h-5" /></div>
      <div className="mt-3 font-heading font-bold text-3xl text-ink flex items-baseline gap-1.5">
        <span>{Number(avg || 0).toFixed(2)}</span>
        <span className="text-base text-ink/40">/5</span>
      </div>
      <div className="text-xs uppercase tracking-wider text-ink/50 mt-1">{label}</div>
      <div className="text-[11px] text-ink/50 mt-0.5">{hint} · {count} response{count === 1 ? "" : "s"}</div>
    </Card>
  );
}

/* ============ LABS ============ */
export function AdminLabs() {
  const [labs, setLabs] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [action, setAction] = useState(null); // { lab, action }
  const [subLab, setSubLab] = useState(null);   // opens the subscription dialog
  const load = () => api.get("/admin/labs").then((r) => setLabs(r.data || []));
  useEffect(() => { load(); }, []);

  const filtered = labs.filter((l) => {
    const status = l.lifecycle_status || "active";
    if (tab !== "all" && status !== tab) return false;
    return !q || (l.name || "").toLowerCase().includes(q.toLowerCase());
  });

  const counts = labs.reduce((a, l) => { const s = l.lifecycle_status || "active"; a[s] = (a[s] || 0) + 1; return a; }, { all: labs.length });

  return (
    <DashboardLayout role="super_admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2"><Building2 className="w-6 h-6 text-efgh-orange" /> Empanelled labs</h1>
          <p className="text-ink/60">{labs.length} labs on the platform · manage lifecycle here.</p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9 h-10 w-64" />
        </div>
      </div>

      {/* Lifecycle filter tabs */}
      <div className="mt-5 flex gap-2 flex-wrap">
        {[
          { id: "all", label: "All" },
          { id: "pending_approval", label: "Pending approval" },
          { id: "active", label: "Active" },
          { id: "paused", label: "Paused" },
          { id: "blocked", label: "Blocked" },
          { id: "removed", label: "Removed" },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`labs-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-3 h-9 rounded-lg text-sm font-medium transition-colors ${tab === t.id ? "bg-ink text-white" : "bg-muted/60 text-ink/70 hover:bg-muted"}`}
          >
            {t.label} <span className="text-[10px] opacity-70 ml-1">({counts[t.id] || 0})</span>
          </button>
        ))}
      </div>

      <Card className="mt-4 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
        <div className="grid grid-cols-[2fr_1fr_0.7fr_0.7fr_1fr_1fr_auto] gap-3 px-5 py-3 text-[11px] uppercase tracking-wider text-ink/50 border-b border-border bg-muted/40 min-w-[820px]">
          <div>Lab</div><div>City</div><div>Reports</div><div>Bookings</div><div>Subscription</div><div>Status</div><div className="text-right">Actions</div>
        </div>
        {filtered.length === 0 && <div className="p-10 text-center text-ink/50">No labs in this bucket.</div>}
        {filtered.map((l) => {
          const initials = (l.name || "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
          const status = l.lifecycle_status || "active";
          return (
            <div key={l.id} data-testid={`admin-lab-${l.id}`} className="grid grid-cols-[2fr_1fr_0.7fr_0.7fr_1fr_1fr_auto] gap-3 px-5 py-3 items-center border-b border-border min-w-[820px]">
              <div className="flex items-center gap-3">
                {l.logo_url ? <img src={l.logo_url} className="w-9 h-9 rounded-lg object-contain bg-muted p-0.5" alt="" /> : <div className="w-9 h-9 rounded-lg bg-ink text-white grid place-items-center text-xs font-heading font-bold">{initials}</div>}
                <div>
                  <Link to={`/admin/labs/${l.id}`} data-testid={`admin-lab-open-${l.id}`} className="font-medium text-ink hover:underline">{l.name}</Link>
                  <div className="text-xs text-ink/50 truncate">{l.email}</div>
                </div>
              </div>
              <div className="text-sm text-ink/70">{l.city || "—"}</div>
              <div className="text-sm text-ink/70">{l.reports_count || 0}</div>
              <div className="text-sm text-ink/70">{l.bookings_count || 0}</div>
              <button
                data-testid={`open-subscription-${l.id}`}
                onClick={() => setSubLab(l)}
                className="text-left group"
              >
                <div className="text-sm font-medium text-ink capitalize group-hover:underline">{l.subscription_plan || "starter"}</div>
                <SubscriptionPill status={l.subscription_status} />
              </button>
              <div>
                <LifecyclePill status={status} />
              </div>
              <div className="flex items-center justify-end gap-1.5">
                <ListenToggle labId={l.id} />
                <CoverPageToggle labId={l.id} />
                <LifecycleActions lab={l} onPick={(a) => setAction({ lab: l, action: a })} />
              </div>
            </div>
          );
        })}
        </div>
      </Card>

      <LifecycleDialog
        payload={action}
        onClose={() => setAction(null)}
        onDone={() => { setAction(null); load(); }}
      />
      <SubscriptionDialog
        lab={subLab}
        onClose={() => setSubLab(null)}
        onDone={() => { setSubLab(null); load(); }}
      />
    </DashboardLayout>
  );
}

function ListenToggle({ labId }) {
  const { platform, refresh } = useSiteSettings();
  const [saving, setSaving] = React.useState(false);
  const globalOn = platform?.listen_enabled !== false;
  const disabledList = platform?.listen_disabled_labs || [];
  const on = globalOn && !disabledList.includes(labId);
  const toggle = async () => {
    setSaving(true);
    const next = new Set(disabledList);
    if (on) next.add(labId); else next.delete(labId);
    try {
      await api.put("/platform-settings", { listen_disabled_labs: Array.from(next) });
      if (refresh) await refresh();
      toast.success(on ? "Listen disabled for this lab" : "Listen enabled for this lab");
    } catch (e) { toast.error(apiError(e, "Update failed")); }
    setSaving(false);
  };
  if (!globalOn) return null;
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={saving}
      data-testid={`admin-lab-listen-${labId}`}
      title={on ? "Listen is ON for this lab — click to disable" : "Listen is DISABLED for this lab — click to enable"}
      className={`w-8 h-8 grid place-items-center rounded-lg border transition-all ${on ? "bg-white border-border text-ink hover:bg-muted" : "bg-muted border-transparent text-ink/40 hover:bg-slate-200"}`}
    >
      {on ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
    </button>
  );
}

/**
 * Super-admin per-lab toggle to grant this lab the ability to upload a PDF cover page
 * that gets prepended to every downloaded Smart Report. Opt-in per lab.
 */
function CoverPageToggle({ labId }) {
  const { platform, refresh } = useSiteSettings();
  const [saving, setSaving] = React.useState(false);
  const enabledList = platform?.cover_page_enabled_labs || [];
  const on = enabledList.includes(labId);
  const toggle = async () => {
    setSaving(true);
    const next = new Set(enabledList);
    if (on) next.delete(labId); else next.add(labId);
    try {
      await api.put("/platform-settings", { cover_page_enabled_labs: Array.from(next) });
      if (refresh) await refresh();
      toast.success(on ? "PDF cover page disabled for this lab" : "PDF cover page enabled — lab can now upload a cover PDF");
    } catch (e) { toast.error(apiError(e, "Update failed")); }
    setSaving(false);
  };
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={saving}
      data-testid={`admin-lab-cover-page-${labId}`}
      title={on ? "PDF cover page is ENABLED for this lab — click to disable" : "PDF cover page is DISABLED for this lab — click to enable"}
      className={`w-8 h-8 grid place-items-center rounded-lg border transition-all ${on ? "bg-efgh-blue/10 border-efgh-blue/40 text-efgh-blue hover:bg-efgh-blue/20" : "bg-muted border-transparent text-ink/40 hover:bg-slate-200"}`}
    >
      <FileImage className="w-3.5 h-3.5" />
    </button>
  );
}

function LifecyclePill({ status }) {
  const map = {
    active: { cls: "pill-normal", label: "Active" },
    pending_approval: { cls: "pill-low", label: "Pending approval" },
    paused: { cls: "pill-borderline", label: "Paused" },
    blocked: { cls: "pill-high", label: "Blocked" },
    removed: { cls: "bg-muted text-ink/50", label: "Removed" },
  };
  const m = map[status] || map.active;
  return <span className={`pill ${m.cls}`}>{m.label}</span>;
}

function SubscriptionPill({ status }) {
  const map = {
    trial: { cls: "pill-low", label: "Trial" },
    active: { cls: "pill-normal", label: "Active" },
    paused: { cls: "pill-borderline", label: "Paused" },
    cancelled: { cls: "bg-muted text-ink/50", label: "Cancelled" },
    pending: { cls: "pill-borderline", label: "Pending" },
  };
  const m = map[status] || map.pending;
  return <span className={`pill ${m.cls} text-[10px] mt-0.5`}>{m.label}</span>;
}

const PLAN_PRESETS = {
  starter:   { label: "Starter",    price: "₹4 per report",   limit: null,  desc: "Pay-as-you-go — no minimum." },
  growth:    { label: "Growth",     price: "₹3,499 / month",  limit: 2000,  desc: "Up to 2,000 reports/month + full white-label." },
  enterprise:{ label: "Enterprise", price: "Custom",          limit: 10000, desc: "10,000+ reports/month · dedicated success manager." },
};

function SubscriptionDialog({ lab, onClose, onDone }) {
  const [plan, setPlan] = useState("starter");
  const [status, setStatus] = useState("trial");
  const [trialEndsAt, setTrialEndsAt] = useState("");
  const [monthlyLimit, setMonthlyLimit] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!lab) return;
    setPlan(lab.subscription_plan || "starter");
    setStatus(lab.subscription_status || "pending");
    setTrialEndsAt((lab.trial_ends_at || "").slice(0, 10));
    setMonthlyLimit(lab.monthly_report_limit ?? "");
    setNote(lab.subscription_note || "");
  }, [lab]);

  const preset = PLAN_PRESETS[plan] || PLAN_PRESETS.starter;

  const save = async () => {
    if (!lab) return;
    setBusy(true);
    try {
      const body = { plan, status, note: note.trim() || null };
      if (trialEndsAt) body.trial_ends_at = trialEndsAt;
      if (monthlyLimit !== "" && monthlyLimit !== null) body.monthly_report_limit = Number(monthlyLimit);
      await api.patch(`/admin/labs/${lab.id}/subscription`, body);
      toast.success(`Subscription updated — ${lab.name}`);
      onDone();
    } catch (e) { toast.error(apiError(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const quickActivate = async () => {
    // Activate current plan immediately with a 30-day trial ending today
    setBusy(true);
    try {
      await api.patch(`/admin/labs/${lab.id}/subscription`, { plan, status: "active", note: note.trim() || "Activated by ops" });
      toast.success(`${lab.name} is now active on ${PLAN_PRESETS[plan]?.label}`);
      onDone();
    } catch (e) { toast.error(apiError(e, "Activation failed")); }
    finally { setBusy(false); }
  };

  if (!lab) return null;

  return (
    <Dialog open={!!lab} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="subscription-dialog" className="max-w-lg">
        <DialogTitle className="font-heading flex items-center gap-2"><CreditCard className="w-5 h-5 text-efgh-orange" /> Manage subscription</DialogTitle>
        <div className="text-sm text-ink/70">{lab.name} · {lab.email}</div>

        <div className="mt-4 grid gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink/50 mb-2">Plan</div>
            <div className="grid grid-cols-3 gap-2" data-testid="plan-picker">
              {Object.entries(PLAN_PRESETS).map(([k, p]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setPlan(k)}
                  data-testid={`plan-${k}`}
                  className={`p-3 rounded-xl border text-left transition-all ${plan === k ? "border-ink bg-ink/[0.04]" : "border-border hover:border-ink/40"}`}
                >
                  <div className="font-medium text-ink text-sm">{p.label}</div>
                  <div className="text-[11px] text-ink/50">{p.price}</div>
                </button>
              ))}
            </div>
            <div className="mt-2 text-xs text-ink/60">{preset.desc}</div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-ink/50 mb-1">Status</div>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="sub-status-trigger" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-ink/50 mb-1">Trial ends</div>
              <Input data-testid="sub-trial-end" type="date" value={trialEndsAt} onChange={(e) => setTrialEndsAt(e.target.value)} className="h-10" />
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-ink/50 mb-1">Monthly report limit</div>
            <Input
              data-testid="sub-monthly-limit"
              type="number"
              min={0}
              placeholder={preset.limit ? String(preset.limit) : "Unlimited (blank)"}
              value={monthlyLimit}
              onChange={(e) => setMonthlyLimit(e.target.value)}
              className="h-10"
            />
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-ink/50 mb-1">Note (audit log)</div>
            <Textarea data-testid="sub-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Verified over call on 2026-02-14 — starting trial" />
          </div>
        </div>

        <div className="mt-5 flex gap-2 justify-between">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <div className="flex gap-2">
            <Button
              onClick={quickActivate}
              disabled={busy}
              variant="outline"
              data-testid="sub-quick-activate"
              className="border-efgh-green text-efgh-green hover:bg-efgh-green/10"
            >
              <Zap className="w-4 h-4 mr-2" /> Activate now
            </Button>
            <Button
              onClick={save}
              disabled={busy}
              data-testid="sub-save"
              className="bg-ink hover:bg-ink/90 text-white"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LifecycleActions({ lab, onPick }) {
  const status = lab.lifecycle_status || "active";
  const actions = [];
  if (status === "pending_approval") actions.push({ id: "approve", label: "Approve", icon: CheckCircle2, tone: "text-efgh-green" });
  if (status === "active") { actions.push({ id: "pause", label: "Pause", icon: Pause, tone: "text-efgh-orange" }); actions.push({ id: "block", label: "Block", icon: Ban, tone: "text-red-600" }); }
  if (status === "paused") { actions.push({ id: "activate", label: "Resume", icon: Play, tone: "text-efgh-green" }); actions.push({ id: "block", label: "Block", icon: Ban, tone: "text-red-600" }); }
  if (status === "blocked") actions.push({ id: "activate", label: "Unblock", icon: ShieldCheck, tone: "text-efgh-green" });
  if (status !== "removed") actions.push({ id: "remove", label: "Remove", icon: Trash2, tone: "text-red-600" });
  if (status === "removed") actions.push({ id: "activate", label: "Restore", icon: Play, tone: "text-efgh-green" });
  return (
    <>
      {actions.map((a) => (
        <button
          key={a.id}
          data-testid={`life-${a.id}-${lab.id}`}
          onClick={() => onPick(a.id)}
          className={`inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-border bg-white hover:bg-muted text-xs ${a.tone}`}
          title={a.label}
        >
          <a.icon className="w-3.5 h-3.5" /> <span className="hidden md:inline">{a.label}</span>
        </button>
      ))}
    </>
  );
}

const ACTION_COPY = {
  approve: { title: "Approve lab", cta: "Approve", tone: "bg-efgh-green hover:bg-efgh-green/90", desc: "Approve this lab. Their staff will get instant dashboard access and their tests will surface in the marketplace." },
  activate: { title: "Activate lab", cta: "Activate", tone: "bg-efgh-green hover:bg-efgh-green/90", desc: "Bring this lab back online. Marketplace + dashboard will resume." },
  pause: { title: "Pause lab", cta: "Pause", tone: "bg-efgh-orange hover:bg-efgh-orange/90", desc: "Temporarily hide from the marketplace and freeze new uploads/publishes. Existing reports remain visible to patients." },
  block: { title: "Block lab", cta: "Block", tone: "bg-red-600 hover:bg-red-700", desc: "Immediately blocks all lab-admin access AND hides them from the marketplace. Use for compliance / trust issues." },
  remove: { title: "Remove lab", cta: "Remove", tone: "bg-red-600 hover:bg-red-700", desc: "Soft-delete this lab. Historical reports keep resolving but the lab disappears from all lists. Restore later if needed." },
};

function LifecycleDialog({ payload, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNote(""); }, [payload?.lab?.id, payload?.action]);
  if (!payload) return null;
  const { lab, action } = payload;
  const copy = ACTION_COPY[action] || ACTION_COPY.pause;

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/labs/${lab.id}/lifecycle`, { action, note: note.trim() || null });
      toast.success(`${copy.cta} — ${lab.name}`);
      onDone();
    } catch (e) { toast.error(apiError(e, "Lifecycle change failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!payload} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="lifecycle-dialog" className="max-w-md">
        <DialogTitle className="font-heading">{copy.title}</DialogTitle>
        <div className="text-sm text-ink/70">{lab.name}</div>
        <div className="mt-3 text-sm text-ink/80 leading-relaxed">{copy.desc}</div>
        <div className="mt-4">
          <Textarea rows={3} placeholder="Note (optional, saved to audit log)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="lifecycle-note" />
        </div>
        <div className="mt-4 flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={confirm} disabled={busy} data-testid="lifecycle-confirm" className={`text-white ${copy.tone}`}>
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} {copy.cta}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ============ BOOKINGS ============ */
export function AdminBookings() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/bookings").then((r) => setRows(r.data || [])); }, []);
  return (
    <DashboardLayout role="super_admin">
      <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2"><Calendar className="w-6 h-6 text-efgh-orange" /> All bookings</h1>
      <p className="text-ink/60">Live view of every booking flowing through EFGH.</p>
      <Card className="mt-6 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
        <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr_1fr_1fr] gap-3 px-5 py-3 text-[11px] uppercase tracking-wider text-ink/50 border-b border-border bg-muted/40 min-w-[720px]">
          <div>Ref / Patient</div><div>Lab</div><div>Slot</div><div>Amount</div><div>Payment</div><div>Status</div>
        </div>
        {rows.length === 0 && <div className="p-10 text-center text-ink/50">No bookings yet.</div>}
        {rows.map((b) => (
          <div key={b.id} className="grid grid-cols-[1.4fr_1fr_1fr_1fr_1fr_1fr] gap-3 px-5 py-3 items-center border-b border-border min-w-[720px]" data-testid={`ab-${b.id}`}>
            <div><div className="text-xs text-ink/50 font-mono">{b.id.slice(0, 8).toUpperCase()}</div><div className="font-medium text-ink">{b.patient_name}</div></div>
            <div className="text-sm text-ink/70">{b.lab_id ? b.lab_id.slice(0, 8) : "—"}</div>
            <div className="text-sm text-ink/70">{b.slot_date} · {b.slot_time}</div>
            <div className="font-heading font-semibold text-ink">₹{b.total_amount}</div>
            <div><span className="pill pill-low text-[10px]">{(b.payment_method || "at_lab").replace("_", " ")}</span></div>
            <div><span className={`pill ${b.booking_status === "delivered" ? "pill-normal" : b.booking_status === "cancelled" ? "pill-high" : "pill-borderline"}`}>{(b.booking_status || "booked").replace(/_/g, " ")}</span></div>
          </div>
        ))}
        </div>
      </Card>
    </DashboardLayout>
  );
}

/* ============ REPORTS ============ */
export function AdminReports() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/reports").then((r) => setRows(r.data || [])); }, []);
  return (
    <DashboardLayout role="super_admin">
      <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2"><FileText className="w-6 h-6 text-efgh-blue" /> All reports</h1>
      <Card className="mt-6 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
        <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr_0.8fr] gap-3 px-5 py-3 text-[11px] uppercase tracking-wider text-ink/50 border-b border-border bg-muted/40 min-w-[680px]">
          <div>Patient</div><div>Lab</div><div>Package</div><div>Created</div><div>Status</div>
        </div>
        {rows.map((r) => (
          <Link key={r.id} to={`/report/${r.id}`} className="grid grid-cols-[1.4fr_1fr_1fr_1fr_0.8fr] gap-3 px-5 py-3 items-center border-b border-border hover:bg-muted/40 min-w-[680px]" data-testid={`ar-${r.id}`}>
            <div className="font-medium text-ink">{r.patient_name}</div>
            <div className="text-sm text-ink/70 truncate">{r.lab_name}</div>
            <div className="text-sm text-ink/70 truncate">{r.package_name || "—"}</div>
            <div className="text-xs text-ink/50">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</div>
            <div><span className={`pill ${(r.status === "ready" || r.status === "published") ? "pill-normal" : r.status === "failed" ? "pill-high" : "pill-borderline"}`}>{r.status}</span></div>
          </Link>
        ))}
        </div>
      </Card>
    </DashboardLayout>
  );
}

/* ============ BILLING ============ */
export function AdminBilling() {
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "subscriptions");
  const [clientKind, setClientKind] = useState("labs");
  const [clients, setClients] = useState({ total: 0, rows: [] });
  const [clientQuery, setClientQuery] = useState("");

  useEffect(() => { api.get("/admin/invoices").then((r) => setInvoices(r.data || [])); }, []);
  useEffect(() => { api.get("/admin/billing/summary").then((r) => setSummary(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (tab !== "clients") return;
    const q = clientQuery ? `&q=${encodeURIComponent(clientQuery)}` : "";
    api.get(`/admin/billing/clients?kind=${clientKind}${q}`).then((r) => setClients(r.data)).catch(() => {});
  }, [tab, clientKind, clientQuery]);

  const paid = invoices.filter((i) => i.status === "paid").reduce((s, i) => s + i.amount, 0);
  const due = invoices.filter((i) => i.status === "due").reduce((s, i) => s + i.amount, 0);
  const totalBilling = summary?.monthly_billing_total ?? (paid + due);
  const mktCommission = summary?.marketplace?.commission ?? 0;
  const fupCommission = summary?.followup?.commission ?? 0;
  const nearExpiry = summary?.near_expiry || [];

  return (
    <DashboardLayout role="super_admin">
      <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2"><CreditCard className="w-6 h-6 text-efgh-yellow" /> Billing & subscriptions</h1>
      <p className="text-ink/60">Recurring revenue, marketplace & follow-up commissions, near-expiry alerts, and every client on the platform.</p>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-billing-kpis">
        <Kpi icon={IndianRupee} color="text-efgh-yellow" bg="bg-efgh-yellow/10" value={`₹${(totalBilling/1000).toFixed(1)}K`} label="Total monthly billing" onClick={() => setTab("subscriptions")} testid="admin-kpi-monthly-billing" />
        <Kpi icon={Calendar}    color="text-efgh-orange" bg="bg-efgh-orange/10" value={nearExpiry.length} label="Near-expiry subscriptions" onClick={() => setTab("subscriptions")} testid="admin-kpi-near-expiry" />
        <Kpi icon={CheckCircle2} color="text-efgh-green" bg="bg-efgh-green/10" value={`₹${(mktCommission/1000).toFixed(1)}K`} label="Marketplace commission (MTD)" onClick={() => setTab("commissions")} testid="admin-kpi-mkt-comm" />
        <Kpi icon={CheckCircle2} color="text-efgh-blue" bg="bg-efgh-blue/10" value={`₹${(fupCommission/1000).toFixed(1)}K`} label="Follow-up commission (MTD)" onClick={() => setTab("commissions")} testid="admin-kpi-fup-comm" />
      </div>

      <div className="mt-6 flex gap-1 p-1 bg-muted rounded-full w-fit" data-testid="billing-tabs">
        {[
          { id: "subscriptions", label: "Subscriptions" },
          { id: "commissions", label: "Commissions" },
          { id: "clients", label: "Clients" },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`bill-tab-${t.id}`}
            className={`px-4 py-1.5 text-xs uppercase tracking-widest rounded-full font-semibold transition-all ${tab === t.id ? "bg-white text-ink shadow-sm" : "text-ink/50 hover:text-ink"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "subscriptions" && (
        <>
          {nearExpiry.length > 0 && (
            <Card className="mt-4 p-5 rounded-2xl border-amber-300 bg-amber-50" data-testid="near-expiry-panel">
              <div className="font-heading font-semibold text-ink flex items-center gap-2"><Calendar className="w-4 h-4 text-amber-600" /> Near-expiry subscriptions ({nearExpiry.length})</div>
              <div className="mt-3 grid md:grid-cols-2 gap-2">
                {nearExpiry.map((e) => (
                  <Link key={e.lab_id} to={`/admin/labs/${e.lab_id}`} className="p-3 rounded-xl bg-white border border-amber-200 hover:border-amber-400 transition-colors" data-testid={`ne-${e.lab_id}`}>
                    <div className="font-medium text-ink">{e.lab_name}</div>
                    <div className="text-xs text-ink/60">{(e.plan || "—").toUpperCase()} · {e.billing || "monthly"} · <b className={e.days_left <= 3 ? "text-red-600" : "text-amber-700"}>{e.days_left != null ? `${e.days_left} day${e.days_left === 1 ? "" : "s"} left` : String(e.expires_at || "").slice(0, 10)}</b></div>
                  </Link>
                ))}
              </div>
            </Card>
          )}
          <Card className="mt-4 rounded-2xl overflow-hidden">
            <div className="grid grid-cols-[1.4fr_1fr_0.7fr_0.7fr_0.7fr] gap-3 px-5 py-3 text-[11px] uppercase tracking-wider text-ink/50 border-b border-border bg-muted/40">
              <div>Invoice</div><div>Lab</div><div>Plan</div><div>Amount</div><div>Status</div>
            </div>
            {invoices.map((i) => (
              <div key={i.id} className="grid grid-cols-[1.4fr_1fr_0.7fr_0.7fr_0.7fr] gap-3 px-5 py-3 items-center border-b border-border" data-testid={`inv-${i.id}`}>
                <div><div className="font-mono text-sm text-ink">{i.id}</div><div className="text-xs text-ink/50">{i.date}</div></div>
                <div className="text-sm text-ink/70 truncate">{i.lab_name}</div>
                <div className="text-sm text-ink/70 capitalize">{i.plan}</div>
                <div className="font-heading font-semibold text-ink">₹{i.amount}</div>
                <div><span className={`pill ${i.status === "paid" ? "pill-normal" : "pill-borderline"}`}>{i.status}</span></div>
              </div>
            ))}
            {invoices.length === 0 && <div className="p-10 text-center text-ink/50">No invoices yet.</div>}
          </Card>
        </>
      )}

      {tab === "commissions" && summary && (
        <div className="mt-4 grid md:grid-cols-2 gap-4" data-testid="commissions-panel">
          <Card className="p-5 rounded-2xl">
            <div className="text-[11px] uppercase tracking-widest text-efgh-green font-heading font-semibold">Marketplace bookings</div>
            <div className="mt-2 flex items-baseline gap-2">
              <div className="font-heading font-bold text-3xl text-ink">₹{(summary.marketplace.commission).toLocaleString()}</div>
              <div className="text-sm text-ink/50">this month</div>
            </div>
            <div className="mt-1 text-sm text-ink/60">GMV: <b className="text-ink">₹{summary.marketplace.gmv.toLocaleString()}</b> · platform default {summary.marketplace.default_percent}%</div>
            <Link to="/admin/pricing" className="mt-4 inline-block text-xs text-efgh-orange hover:underline" data-testid="edit-mkt-comm">Edit platform default →</Link>
          </Card>
          <Card className="p-5 rounded-2xl">
            <div className="text-[11px] uppercase tracking-widest text-efgh-blue font-heading font-semibold">Follow-up bookings</div>
            <div className="mt-2 flex items-baseline gap-2">
              <div className="font-heading font-bold text-3xl text-ink">₹{(summary.followup.commission).toLocaleString()}</div>
              <div className="text-sm text-ink/50">this month</div>
            </div>
            <div className="mt-1 text-sm text-ink/60">GMV: <b className="text-ink">₹{summary.followup.gmv.toLocaleString()}</b> · platform default {summary.followup.default_percent}%</div>
            <Link to="/admin/pricing" className="mt-4 inline-block text-xs text-efgh-orange hover:underline" data-testid="edit-fup-comm">Edit platform default →</Link>
          </Card>
        </div>
      )}

      {tab === "clients" && (
        <div className="mt-4" data-testid="clients-panel">
          <div className="flex flex-wrap gap-2 items-center mb-3">
            <div className="flex gap-1 p-1 bg-muted rounded-full">
              {["labs", "individuals"].map((k) => (
                <button key={k} onClick={() => setClientKind(k)} data-testid={`client-kind-${k}`}
                  className={`px-3 py-1 text-xs uppercase tracking-widest rounded-full font-semibold ${clientKind === k ? "bg-white text-ink" : "text-ink/50"}`}>{k}</button>
              ))}
            </div>
            <input value={clientQuery} onChange={(e) => setClientQuery(e.target.value)} placeholder={`Search ${clientKind}…`} className="text-sm px-3 py-1.5 border border-border rounded-lg" data-testid="client-search" />
            <span className="text-xs text-ink/50 ml-auto">{clients.total} total</span>
          </div>
          <Card className="rounded-2xl overflow-hidden">
            {clientKind === "labs" ? (
              <>
                <div className="grid grid-cols-[1.4fr_0.7fr_0.7fr_0.9fr_0.9fr_0.7fr] gap-3 px-5 py-3 text-[11px] uppercase tracking-wider text-ink/50 border-b border-border bg-muted/40">
                  <div>Lab</div><div>Plan</div><div>Reports</div><div>Marketplace comm.</div><div>Follow-up comm.</div><div>Status</div>
                </div>
                {clients.rows.map((l) => (
                  <Link key={l.id} to={`/admin/labs/${l.id}`} className="grid grid-cols-[1.4fr_0.7fr_0.7fr_0.9fr_0.9fr_0.7fr] gap-3 px-5 py-3 items-center border-b border-border hover:bg-muted/30" data-testid={`client-lab-${l.id}`}>
                    <div><div className="font-medium text-ink">{l.name}</div><div className="text-xs text-ink/50">{l.slug}</div></div>
                    <div className="text-sm capitalize">{l.subscription_plan || "—"}</div>
                    <div className="text-sm text-ink/60">{l.reports_count}</div>
                    <div className="text-sm">{l.effective_marketplace_percent}% {l.marketplace_commission_percent != null && <span className="text-[10px] text-efgh-orange font-semibold ml-1">override</span>}</div>
                    <div className="text-sm">{l.effective_followup_percent}% {l.followup_commission_percent != null && <span className="text-[10px] text-efgh-orange font-semibold ml-1">override</span>}</div>
                    <div><span className={`pill text-[10px] ${l.lifecycle_status === "active" ? "pill-normal" : "pill-borderline"}`}>{l.lifecycle_status || l.subscription_status}</span></div>
                  </Link>
                ))}
              </>
            ) : (
              <>
                <div className="grid grid-cols-[1.5fr_1fr_0.7fr_0.7fr] gap-3 px-5 py-3 text-[11px] uppercase tracking-wider text-ink/50 border-b border-border bg-muted/40">
                  <div>Patient</div><div>Contact</div><div>Wallet</div><div>Reports</div>
                </div>
                {clients.rows.map((u) => (
                  <Link key={u.id} to={`/admin/users/${u.id}`} className="grid grid-cols-[1.5fr_1fr_0.7fr_0.7fr] gap-3 px-5 py-3 items-center border-b border-border hover:bg-muted/30" data-testid={`client-user-${u.id}`}>
                    <div className="font-medium text-ink">{u.full_name || u.email}</div>
                    <div className="text-xs text-ink/60 truncate">{u.email}{u.phone ? ` · ${u.phone}` : ""}</div>
                    <div className="text-sm">{u.wallet_coins} <span className="text-xs text-ink/50">coins</span></div>
                    <div className="text-sm text-ink/60">{u.reports_count}</div>
                  </Link>
                ))}
              </>
            )}
            {clients.rows.length === 0 && <div className="p-10 text-center text-ink/50">No {clientKind} match.</div>}
          </Card>
        </div>
      )}
    </DashboardLayout>
  );
}

/* ============ MARKETING EDITOR ============ */
export function AdminMarketing() {
  const { refresh: refreshSiteSettings } = useSiteSettings();
  const [content, setContent] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => { api.get("/marketing/content").then((r) => setContent(r.data)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/marketing/content", { content });
      toast.success("Marketing content saved");
      setDirty(false);
      // Re-fetch global settings so live app picks up branding immediately
      refreshSiteSettings();
    }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!content) return <DashboardLayout role="super_admin"><div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-ink/40 mx-auto" /></div></DashboardLayout>;

  const patch = (path, val) => {
    setContent((c) => {
      const next = structuredClone(c);
      const parts = path.split(".");
      let ptr = next;
      for (let i = 0; i < parts.length - 1; i++) ptr = ptr[parts[i]];
      ptr[parts[parts.length - 1]] = val;
      return next;
    });
    setDirty(true);
  };

  const uploadLogo = async (file, field) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo too large (max 2 MB)"); return; }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = r.data.url;
      patch(field, url);
      toast.success("Logo uploaded — remember to Publish");
    } catch (e) { toast.error(apiError(e, "Upload failed")); }
    finally { setUploadingLogo(false); }
  };

  return (
    <DashboardLayout role="super_admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2"><Sparkles className="w-6 h-6 text-efgh-yellow" /> Marketing site editor</h1>
          <p className="text-ink/60">Live-edit the EFGH landing page. Changes go live immediately — no code deploy.</p>
        </div>
        {dirty && <Button onClick={save} disabled={saving} data-testid="save-marketing" className="bg-ink hover:bg-ink/90 text-white">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Publish</Button>}
      </div>

      <div className="mt-6 grid gap-6">
        {/* ===== Site Branding: logo, font, colors ===== */}
        <Section title="Site Branding (Logo · Font · Colors)" icon={Palette}>
          <div className="grid md:grid-cols-2 gap-4">
            <LogoUploader
              label="Primary logo (light backgrounds)"
              url={content.logo_url}
              onFile={(f) => uploadLogo(f, "logo_url")}
              onClear={() => patch("logo_url", "/efgh-logo.png")}
              uploading={uploadingLogo}
              testid="logo-primary"
            />
            <LogoUploader
              label="White logo (dark backgrounds)"
              url={content.logo_url_white}
              onFile={(f) => uploadLogo(f, "logo_url_white")}
              onClear={() => patch("logo_url_white", "/efgh-logo-white.png")}
              uploading={uploadingLogo}
              dark
              testid="logo-white"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-3 mt-4">
            <div>
              <label className="text-xs text-ink/60 uppercase tracking-wider">Font family</label>
              <select
                data-testid="font-family-select"
                value={content.font_family || "Inter"}
                onChange={(e) => patch("font_family", e.target.value)}
                className="w-full h-10 px-3 border border-input rounded-md text-sm bg-white"
              >
                {AVAILABLE_FONTS.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
              </select>
              <div className="mt-2 p-3 rounded-lg bg-muted text-lg" style={{ fontFamily: `"${content.font_family || "Inter"}"` }}>
                Aa · The quick brown fox 12345
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <ColorField label="Primary" v={content.brand_primary_color} onChange={(v) => patch("brand_primary_color", v)} testid="color-primary" />
              <ColorField label="Secondary" v={content.brand_secondary_color} onChange={(v) => patch("brand_secondary_color", v)} testid="color-secondary" />
              <ColorField label="Accent" v={content.brand_accent_color} onChange={(v) => patch("brand_accent_color", v)} testid="color-accent" />
              <ColorField label="Ink (text)" v={content.brand_ink_color} onChange={(v) => patch("brand_ink_color", v)} testid="color-ink" />
            </div>
          </div>
        </Section>

        {/* ===== WhatsApp booking number ===== */}
        <Section title="WhatsApp Booking Chat" icon={MessageCircle}>
          <div className="grid md:grid-cols-[1fr_auto] gap-3 items-end">
            <Field
              label="WhatsApp number (with country code, no + or spaces)"
              v={content.whatsapp_number}
              onChange={(v) => patch("whatsapp_number", String(v).replace(/[^0-9]/g, ""))}
              testid="wa-number"
            />
            {content.whatsapp_number && (
              <a
                data-testid="wa-preview"
                href={`https://wa.me/${String(content.whatsapp_number).replace(/[^0-9]/g, "")}?text=Test%20message%20from%20EFGH%20admin`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center h-10 px-4 rounded-md border border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10 text-sm font-medium transition-colors"
              >
                <MessageCircle className="w-4 h-4 mr-2" /> Test chat
              </a>
            )}
          </div>
          <p className="text-xs text-ink/50">Used by the &quot;Book via WhatsApp&quot; concierge button on the booking marketplace and booking confirmations when a lab has no dedicated number.</p>
        </Section>

        {/* ===== Editable CTAs ===== */}
        <Section title="Buttons &amp; CTAs" icon={Link2}>
          <p className="text-xs text-ink/50 -mt-2 mb-2">Edit both the visible text and the destination URL. Use a full URL (https://…) for external links, or a path (/book-tests, #contact) for in-app navigation.</p>
          <div className="grid md:grid-cols-2 gap-4">
            <CtaEditor
              title="Top-nav ‘Book a Test’ button"
              labelKey="nav_book_label"
              urlKey="nav_book_url"
              content={content}
              onPatch={patch}
              testidBase="nav-book"
            />
            <CtaEditor
              title="Hero ‘Book a Test’ button"
              labelKey="hero_book_label"
              urlKey="hero_book_url"
              content={content}
              onPatch={patch}
              testidBase="hero-book"
            />
            <CtaEditor
              title="Hero Primary CTA (For Labs)"
              labelPath="hero.cta_primary"
              urlKey="hero_cta_primary_url"
              content={content}
              onPatch={patch}
              testidBase="hero-primary"
            />
            <CtaEditor
              title="Hero Secondary CTA (Demo)"
              labelPath="hero.cta_secondary"
              urlKey="hero_cta_secondary_url"
              content={content}
              onPatch={patch}
              testidBase="hero-secondary"
            />
          </div>
        </Section>

        {/* ===== Referral program defaults ===== */}
        <Section title="Referral Program (platform default)" icon={GiftIcon}>
          <p className="text-xs text-ink/50 -mt-2 mb-3">Set the default "Refer & Earn" rewards for EFGH. Each lab can override this for its own patients under Lab → Branding.</p>
          <label className="flex items-center gap-3 p-3 rounded-xl border border-border mb-3">
            <input type="checkbox" data-testid="admin-referral-enabled" checked={!!(content.referral_program?.enabled ?? true)} onChange={(e) => patch("referral_program.enabled", e.target.checked)} className="w-4 h-4" />
            <div className="flex-1">
              <div className="font-medium text-ink text-sm">Enable referral program platform-wide</div>
              <div className="text-xs text-ink/50">Turning this off hides "Refer & Earn" everywhere unless a lab has its own override.</div>
            </div>
          </label>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-4 rounded-xl border border-border">
              <div className="text-[11px] uppercase tracking-widest text-ink/50 font-semibold">Referrer reward (existing patient)</div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <Field label="Percent off" v={content.referral_program?.referrer_percent ?? 0} onChange={(v) => patch("referral_program.referrer_percent", parseFloat(v) || 0)} type="number" testid="admin-rr-percent" />
                <Field label="Flat ₹ off" v={content.referral_program?.referrer_flat ?? 0} onChange={(v) => patch("referral_program.referrer_flat", parseFloat(v) || 0)} type="number" testid="admin-rr-flat" />
              </div>
            </div>
            <div className="p-4 rounded-xl border border-border">
              <div className="text-[11px] uppercase tracking-widest text-ink/50 font-semibold">Referee reward (new patient)</div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <Field label="Percent off" v={content.referral_program?.referee_percent ?? 0} onChange={(v) => patch("referral_program.referee_percent", parseFloat(v) || 0)} type="number" testid="admin-re-percent" />
                <Field label="Flat ₹ off" v={content.referral_program?.referee_flat ?? 0} onChange={(v) => patch("referral_program.referee_flat", parseFloat(v) || 0)} type="number" testid="admin-re-flat" />
              </div>
            </div>
          </div>
          <div className="mt-3">
            <Field label="Program description" v={content.referral_program?.description ?? ""} onChange={(v) => patch("referral_program.description", v)} testid="admin-ref-description" />
          </div>
          <div className="mt-3">
            <Field label="Terms &amp; conditions" v={content.referral_program?.terms ?? ""} onChange={(v) => patch("referral_program.terms", v)} textarea testid="admin-ref-terms" />
          </div>
        </Section>

        {/* ===== Review program defaults ===== */}
        <Section title="Review Wizard (platform default)" icon={StarIcon}>
          <p className="text-xs text-ink/50 -mt-2 mb-3">Default emoji-review settings. Each lab can override its own external URL &amp; threshold under Lab → Patient Reviews.</p>
          <label className="flex items-center gap-3 p-3 rounded-xl border border-border mb-3">
            <input type="checkbox" data-testid="admin-review-enabled" checked={!!(content.review_program?.enabled ?? true)} onChange={(e) => patch("review_program.enabled", e.target.checked)} className="w-4 h-4" />
            <div className="flex-1">
              <div className="font-medium text-ink text-sm">Enable emoji review wizard on Smart Reports</div>
              <div className="text-xs text-ink/50">Off = no wizard is shown unless a lab has its own override.</div>
            </div>
          </label>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Default external review URL (fallback)" v={content.review_program?.external_url ?? ""} onChange={(v) => patch("review_program.external_url", v)} testid="admin-review-url" />
            <Field label="Redirect threshold (1-5)" v={content.review_program?.redirect_threshold ?? 4} onChange={(v) => patch("review_program.redirect_threshold", parseInt(v, 10) || 4)} type="number" testid="admin-review-threshold" />
          </div>
          <div className="mt-3">
            <Field label="Thank-you message (shown after low ratings)" v={content.review_program?.thank_you_message ?? ""} onChange={(v) => patch("review_program.thank_you_message", v)} testid="admin-review-thankyou" />
          </div>
        </Section>

        <Section title="Hero" icon={Sparkles}>
          <Field label="Badge text" v={content.hero.badge} onChange={(v) => patch("hero.badge", v)} testid="mk-hero-badge" />
          <Field label="Title line 1" v={content.hero.title_l1} onChange={(v) => patch("hero.title_l1", v)} testid="mk-hero-l1" />
          <Field label="Title line 2 (accent)" v={content.hero.title_l2} onChange={(v) => patch("hero.title_l2", v)} testid="mk-hero-l2" />
          <Field label="Subtitle" v={content.hero.subtitle} onChange={(v) => patch("hero.subtitle", v)} textarea />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Primary CTA" v={content.hero.cta_primary} onChange={(v) => patch("hero.cta_primary", v)} />
            <Field label="Secondary CTA" v={content.hero.cta_secondary} onChange={(v) => patch("hero.cta_secondary", v)} />
          </div>
        </Section>

        <Section title="Stats band">
          {content.stats.map((s, i) => (
            <div key={i} className="grid grid-cols-2 gap-3 mb-3">
              <Field label={`Stat ${i + 1} · number`} v={s.k} onChange={(v) => patch(`stats.${i}.k`, v)} />
              <Field label={`Stat ${i + 1} · caption`} v={s.v} onChange={(v) => patch(`stats.${i}.v`, v)} />
            </div>
          ))}
        </Section>

        <Section title="Sections copy">
          <Field label="Features heading" v={content.features_heading} onChange={(v) => patch("features_heading", v)} />
          <Field label="Features subheading" v={content.features_subheading} onChange={(v) => patch("features_subheading", v)} textarea />
          <Field label="Pricing heading" v={content.pricing_heading} onChange={(v) => patch("pricing_heading", v)} />
          <Field label="Pricing subheading" v={content.pricing_subheading} onChange={(v) => patch("pricing_subheading", v)} textarea />
          <Field label="Pricing footer (small text under plan cards)" v={content.pricing_footer} onChange={(v) => patch("pricing_footer", v)} textarea rows={2} testid="pricing-footer" />
          <Field label="Contact heading" v={content.contact_heading} onChange={(v) => patch("contact_heading", v)} />
          <Field label="Contact subheading" v={content.contact_subheading} onChange={(v) => patch("contact_subheading", v)} textarea />
        </Section>

        <Section title="Footer" icon={Link2}>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Copyright text" v={content.footer_copyright} onChange={(v) => patch("footer_copyright", v)} testid="footer-copyright" />
            <Field label="Contact email" v={content.footer_email} onChange={(v) => patch("footer_email", v)} testid="footer-email" />
          </div>
          <Field label="Tagline (small)" v={content.footer_tagline} onChange={(v) => patch("footer_tagline", v)} testid="footer-tagline" />
        </Section>

        <Section title="Pricing plans">
          {content.plans.map((p, i) => (
            <div key={i} className="p-4 rounded-xl border border-border mb-3">
              <div className="grid grid-cols-3 gap-2">
                <Field label="Name" v={p.name} onChange={(v) => patch(`plans.${i}.name`, v)} />
                <Field label="Price" v={p.price} onChange={(v) => patch(`plans.${i}.price`, v)} />
                <Field label="Cadence" v={p.cadence} onChange={(v) => patch(`plans.${i}.cadence`, v)} />
              </div>
              <Field label="Sub-line" v={p.sub} onChange={(v) => patch(`plans.${i}.sub`, v)} />
              <Field label="Features (one per line)" v={(p.features || []).join("\n")} onChange={(v) => patch(`plans.${i}.features`, v.split("\n").filter(Boolean))} textarea rows={4} />
              <Field label="CTA text" v={p.cta} onChange={(v) => patch(`plans.${i}.cta`, v)} />
            </div>
          ))}
        </Section>

        <Section title="Testimonials">
          {content.testimonials.map((t, i) => (
            <div key={i} className="p-4 rounded-xl border border-border mb-3">
              <Field label="Quote" v={t.quote} onChange={(v) => patch(`testimonials.${i}.quote`, v)} textarea rows={2} />
              <div className="grid grid-cols-3 gap-2">
                <Field label="Name" v={t.by} onChange={(v) => patch(`testimonials.${i}.by`, v)} />
                <Field label="Role" v={t.role} onChange={(v) => patch(`testimonials.${i}.role`, v)} />
                <Field label="Initials" v={t.initials} onChange={(v) => patch(`testimonials.${i}.initials`, v)} />
              </div>
            </div>
          ))}
        </Section>

        <Section title="FAQ">
          {content.faqs.map((f, i) => (
            <div key={i} className="p-4 rounded-xl border border-border mb-3">
              <Field label="Question" v={f.q} onChange={(v) => patch(`faqs.${i}.q`, v)} />
              <Field label="Answer" v={f.a} onChange={(v) => patch(`faqs.${i}.a`, v)} textarea rows={3} />
            </div>
          ))}
        </Section>
      </div>
    </DashboardLayout>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <Card className="rounded-2xl p-6">
      <div className="font-heading font-semibold text-ink text-lg mb-4 flex items-center gap-2">
        {Icon && <Icon className="w-5 h-5 text-ink/60" />}
        {title}
      </div>
      <div className="space-y-3">{children}</div>
    </Card>
  );
}
function Field({ label, v, onChange, textarea, rows, testid, type }) {
  return (
    <div>
      <label className="text-xs text-ink/60 uppercase tracking-wider">{label}</label>
      {textarea
        ? <Textarea rows={rows || 2} value={v ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
        : <Input type={type || "text"} value={v ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />}
    </div>
  );
}

function ColorField({ label, v, onChange, testid }) {
  return (
    <div>
      <label className="text-xs text-ink/60 uppercase tracking-wider">{label}</label>
      <div className="flex items-center gap-2 mt-1">
        <input
          type="color"
          value={v || "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-14 rounded-md border border-input cursor-pointer bg-transparent"
          data-testid={`${testid}-picker`}
        />
        <Input
          value={v || ""}
          onChange={(e) => onChange(e.target.value)}
          className="font-mono uppercase text-xs h-10"
          placeholder="#RRGGBB"
          data-testid={`${testid}-hex`}
        />
      </div>
    </div>
  );
}

function LogoUploader({ label, url, onFile, onClear, uploading, dark, testid }) {
  const inputRef = React.useRef(null);
  return (
    <div>
      <label className="text-xs text-ink/60 uppercase tracking-wider">{label}</label>
      <div className={`mt-1 rounded-xl border border-dashed border-border p-4 flex items-center gap-4 ${dark ? "bg-ink" : "bg-muted/40"}`}>
        {url ? (
          <img src={url} alt="logo" className="h-14 max-w-[140px] object-contain" data-testid={`${testid}-preview`} />
        ) : (
          <div className={`h-14 w-24 rounded grid place-items-center text-xs ${dark ? "text-white/40" : "text-ink/40"}`}>No logo</div>
        )}
        <div className="flex flex-col gap-2 flex-1">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }}
            className="hidden"
            data-testid={`${testid}-input`}
          />
          <Button
            type="button"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid={`${testid}-upload-btn`}
            className="bg-ink hover:bg-ink/90 text-white"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UploadIcon className="w-4 h-4 mr-2" />}
            {url ? "Replace" : "Upload"}
          </Button>
          {url && (
            <button
              type="button"
              onClick={onClear}
              className={`text-xs underline ${dark ? "text-white/60" : "text-ink/50"} hover:${dark ? "text-white" : "text-ink"}`}
              data-testid={`${testid}-reset`}
            >
              Reset to default
            </button>
          )}
        </div>
      </div>
      <p className="text-[11px] text-ink/40 mt-1">PNG or SVG. Max 2 MB. Transparent background recommended.</p>
    </div>
  );
}

function CtaEditor({ title, labelKey, labelPath, urlKey, urlPath, content, onPatch, testidBase }) {
  const labelP = labelPath || labelKey;
  const urlP = urlPath || urlKey;
  const getAt = (path) => path.split(".").reduce((o, k) => (o ? o[k] : undefined), content);
  return (
    <div className="p-4 rounded-xl border border-border">
      <div className="text-sm font-medium text-ink mb-2">{title}</div>
      <div className="grid gap-2">
        <div>
          <label className="text-xs text-ink/60 uppercase tracking-wider">Button text</label>
          <Input
            value={getAt(labelP) || ""}
            onChange={(e) => onPatch(labelP, e.target.value)}
            data-testid={`${testidBase}-label`}
          />
        </div>
        <div>
          <label className="text-xs text-ink/60 uppercase tracking-wider">Destination URL</label>
          <Input
            value={getAt(urlP) || ""}
            onChange={(e) => onPatch(urlP, e.target.value)}
            placeholder="/book-tests or https://example.com"
            className="font-mono text-xs"
            data-testid={`${testidBase}-url`}
          />
        </div>
      </div>
    </div>
  );
}
